/**
 * EscalateToChatButton
 *
 * "Continue in Chat" — moves the overlay's room-less conversation into a
 * real room (optionally led by an agent) and hands off to the full `/chat`
 * experience, which already has the room UI this compact side panel
 * deliberately doesn't duplicate (agent working-status indicators, tool
 * call rendering, member management, etc.).
 *
 * The room is a private Solo room, so whoever leads it answers every
 * message: the chosen agent (sent as `leadAgentId`, the way ChatPage's
 * create-room form does it), or Neuro. The conversation so far is posted
 * as the first message (handoff-message.ts), so the room picks up where the
 * panel left off.
 *
 * If the agent couldn't be added (the backend logs that and still creates
 * the room), the panel says so instead of navigating away silently.
 *
 * `/chat` deep-links to a room via `?roomId=` (also used by notification
 * links) — reused here as-is.
 */

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { IChatAdapter } from '@neuro/chat-adapter';
import { useAssistantOverlayStore } from '../../stores/assistantOverlayStore';
import { buildAssistantApiClient } from './assistant-api-client';
import { listCanonicalAgents, agentDisplayName, type CanonicalAgentEntry } from './canonical-agents';
import { buildHandoffMessage } from './handoff-message';
import { currentPageLabel } from './screen-context-summary';

interface CreatedRoom {
  id: string;
  name: string;
  aiSettings?: { leadAgentId?: string | null } | null;
}

function errorMessage(err: unknown, fallback: string): string {
  const e = err as { response?: { data?: { message?: string } }; message?: string };
  return e?.response?.data?.message ?? e?.message ?? fallback;
}

export function EscalateToChatButton({ adapter }: { adapter: IChatAdapter }) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [agents, setAgents] = useState<CanonicalAgentEntry[]>([]);
  const [agentId, setAgentId] = useState('');
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // A room that was created without the chosen agent, waiting for the user to open it.
  const [roomWithoutAgent, setRoomWithoutAgent] = useState<{ id: string; agent: string } | null>(null);

  useEffect(() => {
    if (!open || agents.length > 0) return;
    void listCanonicalAgents().then(setAgents);
  }, [open, agents.length]);

  function openRoom(roomId: string) {
    setOpen(false);
    setRoomWithoutAgent(null);
    navigate(`/chat?roomId=${encodeURIComponent(roomId)}`);
    // The conversation carries on in the room; the panel would only cover it.
    useAssistantOverlayStore.getState().close();
  }

  async function handleStart() {
    setCreating(true);
    setError(null);
    setRoomWithoutAgent(null);
    const api = buildAssistantApiClient();
    const pageLabel = currentPageLabel();
    try {
      const room = await api.post<CreatedRoom>('/api/rooms', {
        name: pageLabel ? `Chat: ${pageLabel}` : 'Assistant chat',
        description: null,
        mode: 'solo',
        visibility: 'private',
        notificationMode: 'direct_only',
        context: 'universal',
        aiEnabled: true,
        ...(agentId ? { agentId, leadAgentId: agentId } : {}),
        systemPromptAddendum: null,
        invites: [],
      });

      const handoff = buildHandoffMessage(adapter.getState().messages, pageLabel);
      if (handoff) {
        try {
          await api.post(`/api/rooms/${encodeURIComponent(room.id)}/messages`, {
            content: handoff,
            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          });
        } catch (err) {
          // The room exists; opening it without the history beats losing it.
          console.warn('[AssistantOverlay] Failed to post the conversation into the room:', err);
        }
      }

      if (agentId && room.aiSettings?.leadAgentId !== agentId) {
        const agent = agents.find((a) => a.id === agentId);
        setRoomWithoutAgent({ id: room.id, agent: agent ? agentDisplayName(agent) : agentId });
        return;
      }
      openRoom(room.id);
    } catch (err) {
      setError(errorMessage(err, 'Failed to start chat.'));
    } finally {
      setCreating(false);
    }
  }

  return (
    <div className="assistant-overlay__escalate">
      <button
        type="button"
        className="assistant-overlay__escalate-trigger"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        title="Continue this conversation in a Chat room, optionally with an agent"
      >
        Continue in Chat
      </button>
      {open && (
        <div className="assistant-overlay__escalate-panel" role="menu">
          <label className="assistant-overlay__escalate-label">
            Agent (optional)
            <select
              value={agentId}
              onChange={(e) => setAgentId(e.target.value)}
              className="assistant-overlay__escalate-select"
            >
              <option value="">Neuro</option>
              {agents.map((a) => (
                <option key={a.id} value={a.id}>
                  {agentDisplayName(a)}
                </option>
              ))}
            </select>
          </label>
          {error && (
            <div className="assistant-overlay__escalate-error" role="alert">
              {error}
            </div>
          )}
          {roomWithoutAgent ? (
            <>
              <div className="assistant-overlay__escalate-error" role="alert">
                Couldn't add {roomWithoutAgent.agent} to the room — Neuro will answer there instead.
              </div>
              <button
                type="button"
                className="assistant-overlay__escalate-start"
                onClick={() => openRoom(roomWithoutAgent.id)}
              >
                Open room
              </button>
            </>
          ) : (
            <button
              type="button"
              className="assistant-overlay__escalate-start"
              onClick={handleStart}
              disabled={creating}
            >
              {creating ? 'Starting…' : 'Start'}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
